import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { each, slice } from "lodash";
import { DateTime } from "luxon";
import numeral from "numeral";
import { ChangeEventHandler, Fragment, Reducer, useCallback, useId, useMemo, useReducer } from "react";
import { useTranslation } from "react-i18next";
import { Button, Navbar, NavbarBrand, Table } from "reactstrap";
import { WorkBook, WorkSheet } from "xlsx";
import { ExtractActionsFromState } from "../types";
import { HandlingType } from "../types/graph";
import CenteredContainer from "./CenteredContainer";
import Loading from "./Loading";
import { GridLayout } from "./ui";


type StorageReportNewProps = {
    terminalName: string
}

type GetReportResponse = {
    data: ReportRow[];
    isOk: boolean;
    message: string;
}

type ReportRow = {
    id: number;
    unitNumber: string;
    checkInDate: string;
    checkOutDate: string | null;
    type: HandlingType | null;
    ship: string;
    dangerousGoods: ReportRowDangerousGoods[];
}

type ReportRowDangerousGoods = {
    adrClass: string;
    unNumber: string;
    packingGroup: string;
    weight: number;
}

type QueryParams = {
    terminalName: string,
    from: string,
    to: string
}
//type RequestParams = {
//    terminalName: string,
//    from?: string,
//    to?: string
//}

type UseStorageQueryProps = {
    terminalName: string,
    from: Date,
    to: Date
}

const useStorageQuery = ({ terminalName, from, to }: UseStorageQueryProps) => {
    return useQuery({
        queryKey: ['storage/report', terminalName, from.toISOString(), to.toISOString()] as const,
        queryFn: ({ queryKey: [_storage, terminalName, from, to], signal }) => axios.get<GetReportResponse>('storage/report', {
            params: { terminalName, from, to }, signal
        }).then((res) => res.data)
    });
}

type ReducerState = {
    from: Date,
    to: Date
};

type ReducerStateActions = ExtractActionsFromState<ReducerState>;

const reducer: Reducer<ReducerState, ReducerStateActions> = (prevState, { type, value }) => ({ ...prevState, [type]: value });

const dgCells = (dg: ReportRowDangerousGoods | undefined) => <>
    <td>{dg?.adrClass ?? '-'}</td>
    <td>{dg?.unNumber ?? '-'}</td>
    <td>{dg?.packingGroup ?? '-'}</td>
    <td>{dg !== undefined && dg.weight > 0 ? `${numeral(dg.weight).format()} kg` : '-'}</td>
</>;

const StorageReportNew = ({ terminalName }: StorageReportNewProps) => {

    const { t } = useTranslation('common');
    //const { terminal } = useContext(TerminalContext);

    //const handlingsCount = useMemo<number>(() => handlings?.length ?? 0, [handlings])
    //const handlingsLastId = useMemo<number>(() => _(handlings).maxBy('checkinDate')?.id ?? 0, [handlings])
    const now = new Date(Date.now());
    const [{ from, to }, dispatch] = useReducer(reducer, { from: new Date(now.getFullYear(), 0, 1), to: new Date(now.getFullYear(), now.getMonth(), now.getDay()) });


    const id = useId();

    const changeHandler: ChangeEventHandler<HTMLInputElement> = useCallback(({ target: { name, valueAsDate } }) => {
        switch (name) {
            case 'from':
            case 'to':
                if (valueAsDate !== null) {
                    dispatch({
                        type: name,
                        value: valueAsDate
                    })
                }
                break;
        }
    }, []);

    const { isLoading, error, data, isFetching } = useStorageQuery({ terminalName, from, to });

    const rows = data?.data || null;

    const colNames = useMemo(() => [
        t('Sigla'),
        t('Classe'),
        t('UN Number'),
        t('Packing Group'),
        t('Peso'),
        t('Data d\'ingresso'),
        t('Data di uscita'),
        t('Tipo'),
        t('Nave')
    ], [t]);

    const table = data?.isOk === true
        ? <Table striped>
            <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white' }}>
                <tr>{colNames.map((colName, i) => <th key={i}>{colName}</th>)}</tr>
            </thead>
            <tbody>
                {data.data.map(h => {
                    const rowSpan = Math.max(1, h.dangerousGoods.length);
                    let type;

                    switch (h.type) {
                        case HandlingType.Boarding:
                            type = 'Imbarco';
                            break;
                        case HandlingType.Unboarding:
                            type = 'Sbarco';
                            break;
                        default:
                            type = '-';
                            break;
                    }

                    const checkInDate = new Date(Date.parse(h.checkInDate));
                    const checkOutDate = h.checkOutDate === null ? null : new Date(Date.parse(h.checkOutDate));

                    return <Fragment key={h.id}>
                        <tr>
                            <td rowSpan={rowSpan}>{h.unitNumber}</td>
                            {dgCells(h.dangerousGoods.at(0))}
                            <td rowSpan={rowSpan}>{`${checkInDate.toLocaleDateString()} ${checkInDate.toLocaleTimeString()}`}</td>
                            <td rowSpan={rowSpan}>{`${checkOutDate?.toLocaleDateString() ?? '-'} ${checkOutDate?.toLocaleTimeString() ?? ''}`}</td>
                            <td rowSpan={rowSpan}>{type}</td>
                            <td rowSpan={rowSpan}>{(h.ship?.trim().length ?? 0) === 0 ? '-' : h.ship}</td>
                        </tr>
                        {(h.dangerousGoods.length > 1) && slice(h.dangerousGoods, 1).map((dg) => <tr>{dgCells(dg)}</tr>)}
                    </Fragment>
                })}

            </tbody>
        </Table >
        : <CenteredContainer>{data?.message}</CenteredContainer>

    const content = isFetching || isLoading
        ? <CenteredContainer><Loading /></CenteredContainer>
        : table;

    const { fromValue, toValue, minTo, minFrom, maxTo, maxFrom } = useMemo(() => {

        const now = Date.now();
        const fromDateTime = DateTime.fromJSDate(from);
        const toDateTime = DateTime.fromJSDate(to);

        const fromValue = fromDateTime.toISODate();
        const toValue = toDateTime.toISODate();

        return {
            fromValue,
            toValue,
            minTo: fromValue,
            minFrom: toDateTime.plus({ year: -1 }).toISODate(),
            maxFrom: toValue,
            maxTo: DateTime.fromJSDate(new Date(Math.min(now, fromDateTime.plus({ year: 1 }).toJSDate().valueOf()))).toISODate()
        };
    }, [from, to]);

    const exportXls = useCallback(() => {
        import('xlsx').then(({ /*default: XLSX,*/ utils: { encode_cell, encode_range }, writeFile }) => {
            let r = 0;

            const gws: WorkSheet = {
                '!merges': []
            };

            each(colNames, (v, c) => {
                gws[encode_cell({ r, c })] = { t: 's', v }
            });

            r++;

            /* make worksheet */
            each(rows, ({ unitNumber, ship, type, checkInDate, checkOutDate, dangerousGoods }) => {

                gws[encode_cell({ r, c: 0 })] = { t: 's', v: unitNumber };

                gws[encode_cell({ r, c: 5 })] = { t: 'd', v: checkInDate };
                gws[encode_cell({ r, c: 6 })] = { t: 'd', v: checkOutDate ?? undefined };
                gws[encode_cell({ r, c: 7 })] = { t: 's', v: type ?? undefined };
                gws[encode_cell({ r, c: 8 })] = { t: 's', v: ship ?? undefined };

                if ((dangerousGoods ?? [] as ReportRowDangerousGoods[]).length > 0) {
                    const sr = r;

                    each(dangerousGoods, ({ adrClass, weight, packingGroup, unNumber }) => {

                        gws[encode_cell({ r, c: 1 })] = { t: 's', v: adrClass ?? undefined };
                        gws[encode_cell({ r, c: 2 })] = { t: 's', v: unNumber ?? undefined };
                        gws[encode_cell({ r, c: 3 })] = { t: 's', v: packingGroup ?? undefined };
                        gws[encode_cell({ r, c: 4 })] = { t: 'n', v: weight ?? undefined, z: '0#" kg"' };

                        r++;
                    });

                    if ((dangerousGoods as ReportRowDangerousGoods[]).length > 1) {
                        const er = r - 1;

                        gws['!merges']?.push({ s: { r: sr, c: 0 }, e: { r: er, c: 0 } });
                        gws['!merges']?.push({ s: { r: sr, c: 5 }, e: { r: er, c: 5 } });
                        gws['!merges']?.push({ s: { r: sr, c: 6 }, e: { r: er, c: 6 } });
                        gws['!merges']?.push({ s: { r: sr, c: 7 }, e: { r: er, c: 7 } });
                        gws['!merges']?.push({ s: { r: sr, c: 8 }, e: { r: er, c: 8 } });
                    }

                } else {
                    gws[encode_cell({ r, c: 1 })] = { t: 's', v: 'Dati mancanti' };
                    gws['!merges']?.push({ s: { r, c: 1 }, e: { r, c: 4 } });

                    r++;
                }
            });

            gws['!ref'] = encode_range({ s: { r: 0, c: 0 }, e: { r: r - 1, c: 8 } });

            const sheetNames = [t('Giacenze')];
            const sheets: Record<string, WorkSheet> = {};

            sheets[t('Giacenze')] = gws;

            writeFile({
                SheetNames: sheetNames,
                Sheets: sheets
            } as WorkBook, `giacenze-${terminalName}-${fromValue}-${toValue}.xlsx`);
        });
    }, [terminalName, toValue, fromValue, colNames, rows, t]);

    const header = (
        <header>
            <Navbar className="navbar ng-white border-bottom box-shadow" style={{ paddingBottom: 0, justifyContent: 'left' }} light>
                <NavbarBrand>
                    {t(`terminal.storageReport.title`)}
                </NavbarBrand>

                <div className="d-flex" style={{ gap: '.5em', alignItems: 'center' }}>
                    <div className="d-flex" style={{ gap: '.5em', alignItems: 'center' }}>
                        <label
                            className="form-label"
                            style={{ margin: 0 }}
                            htmlFor={`from-${id}`}>
                            {t('Da')}
                        </label>
                        <input id={`from-${id}`}
                            value={fromValue ?? undefined} 
                            min={minFrom ?? undefined}
                            max={maxFrom ?? undefined}
                            name="from"
                            className="form-control me-2"
                            type="date"
                            onChange={changeHandler} />
                    </div>
                    <div className="d-flex" style={{ gap: '.5em', alignItems: 'center' }}>
                        <label
                            className="form-label"
                            style={{ margin: 0 }}
                            htmlFor={`to-${id}`}>
                            {t('al')}
                        </label>
                        <input id={`to-${id}`}
                            value={toValue ?? undefined}
                            min={minTo ?? undefined}
                            max={maxTo ?? undefined}
                            name="to"
                            className="form-control me-2"
                            type="date"
                            onChange={changeHandler} />
                    </div>
                </div>
                <div>
                    <Button type='button' onClick={() => exportXls()}>Esporta XLS</Button>
                </div>
            </Navbar>
        </header>);

    return (
        <GridLayout header={header}>{content}</GridLayout>
    );
}

export default StorageReportNew;